import {
  type AffirmationControle,
  type AjoutControle,
  type ChangementTraduction,
  type CodeLibelle,
  type CompteStatut,
  type ControleNoyau,
  type ControleTraduction,
  type IndicateurControle,
  type LongueurControle,
  type MiseEnFormeControle,
  NOYAU_CODES,
  type NoyauCode,
  type PointControle,
  type ReecritureControle,
  controleNoyauSchema,
  noyauCodeSchema,
} from '@rosetta/shared';
import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { ServiceIndisponibleError } from '../erreurs';
import { derniereDuType, lireJson, lireResultat } from './analyse-texte';
import { type CorrespondanceTypes, lireSousTaches } from './etapes';
import { CODE_LANGUE, idSchema, lireLignes } from './h2data';
import type { AppelHttp } from './http';
import { TABLE_TACHE } from './taches';

/**
 * The quality control of a finished translation (#10): what the `rose_task_v5_controle`
 * sub-task of the launch says about the text of each noyau. Tahiti runs it after the
 * translations, as a sub-task of the `rose_task_v5_multi_translation` task:
 *
 *   - the sub-task is named in the `result_json` of the launch (`controle.task_id`) or, when
 *     that is null or does not name it, found by `h2_task_main_id` and the `physical_name` of its
 *     task type, as the analysis does (#9, `analyse-texte.ts`);
 *   - its `result_json` (a JSON string) holds `noyaux[]` (one entry per noyau: `indicateurs[]`,
 *     `compte_statuts`, `longueur`), `longueurs.source` and `inventaire.affirmations[]`.
 *
 * Read on devdb on 2026-10-07 and 2026-10-08, read only: controls 910747 (PSG of launch 910728,
 * one "Bloquant", one "À relire") and 910780 (GSP and PGS of launch 910754, an affirmation
 * "affaiblie"); launch 910563 (plugin 1.0.2) has no control and no `controle` key.
 *
 * Every label is Tahiti's: the `libelle` of an indicator for its status, its `nom` for its
 * title, its `resume`, the `raison` of its points. A code without a dictionary (the `type` of an
 * addition, the `etat` of an affirmation) keeps its code, its underscores turned into spaces.
 * Only "OK", "À relire" and "Bloquant" are known without an indicator that carries them (the
 * counters of `compte_statuts` that no indicator has): the labels Tahiti writes for them.
 *
 * Read leniently, part by part: an indicator, a point, an element or a measure that is not what
 * is expected is left out (`null` for a part), the rest stands. A control that is missing, that
 * failed or whose answer cannot be read gives `null`: the screen says it is not available. A
 * call that fails (network, session) still raises. Nothing is written.
 *
 * A text may have several launches (#11: the noyaux added later), each with a control of its own
 * that covers only its noyaux (#34): `lireControle` below reads one launch, `fusionnerControles`
 * puts the launches of a document together, the most recent launch that covers a noyau giving
 * its control.
 *
 * The changes the translation declares (#28) are in another sub-task: the `result_json` of the
 * `rose_task_v5_traduction` of each noyau (`noyau`, `changements[]` of `type`, `passage`,
 * `description`; read on translation 910742 of launch 910728, read only, 2026-10-12). One read of
 * that table by `h2_task_main_id` gives them for every noyau; read as leniently as the rest (a
 * change that does not fit is left out, a read that fails or gives nothing is `null`, never an
 * error). TO VALIDATE(#28): not played with the account of an end user, and only the types
 * "restructuration" and "explicitation" were seen.
 *
 * TO VALIDATE(#10): none of this was played with the account of an end user (that it may read
 * `rose_task_v5_controle`); the labels of `erreur`, `sans_objet` and `information` were never
 * seen; the shape of `residus` (always empty in what was seen) and the meaning of
 * `titre_invente` and `markdown_ajoute` when they are true.
 */

export const TABLE_CONTROLE = 'rose_task_v5_controle';

/** The labels Tahiti writes for the three statuses it uses (`libelle` of the indicators). */
const LIBELLES_CONNUS: Readonly<Record<string, string>> = {
  bloquant: 'Bloquant',
  a_relire: 'À relire',
  ok: 'OK',
};

/** The three statuses the counters always show, in this order. */
const STATUTS_AFFICHES = ['bloquant', 'a_relire', 'ok'] as const;

/** "cause_inventee" -> "cause inventee": a code that has no label. */
const libelleDuCode = (code: string): string => code.replace(/_/g, ' ').trim();

/** A text of Tahiti: trimmed, and `undefined` when it is empty or not a text. */
const texte = z.preprocess(
  (valeur) => (typeof valeur === 'string' && valeur.trim() !== '' ? valeur.trim() : undefined),
  z.string().optional(),
);

/** A list Tahiti may leave out: each element is read on its own, later. */
const liste = z.preprocess((valeur) => (Array.isArray(valeur) ? valeur : []), z.array(z.unknown()));

/** Reads each element of a list with a schema, leaving out the ones that do not fit. */
function chacun<T>(elements: readonly unknown[], lire: (brut: unknown) => T | null): T[] {
  return elements.flatMap((brut) => {
    const lu = lire(brut);
    return lu === null ? [] : [lu];
  });
}

/* ── Finding the sub-task ────────────────────────────────────────────────────────── */

const referenceSchema = z.object({
  controle: z.object({ task_id: idSchema }).passthrough(),
});

/** The control sub-task, as the launch names it (`controle.task_id`), or `null`. */
function controleDuResultat(resultat: unknown): string | null {
  const lu = referenceSchema.safeParse(resultat);
  return lu.success ? lu.data.controle.task_id : null;
}

/* ── The indicators ──────────────────────────────────────────────────────────────── */

const pointBrutSchema = z.object({ raison: texte, passage: texte });

function lirePoint(brut: unknown): PointControle | null {
  const lu = pointBrutSchema.safeParse(brut);
  if (!lu.success || lu.data.raison === undefined) return null;
  const { raison, passage } = lu.data;
  return { raison, ...(passage === undefined ? {} : { passage }) };
}

const indicateurBrutSchema = z.object({
  numero: z.union([z.string(), z.number()]).transform(String),
  nom: texte,
  statut: texte,
  libelle: texte,
  resume: texte,
  points: liste,
});

/** One indicator, whatever it measures; `null` without a number, a name and a status. */
function lireIndicateur(brut: unknown): IndicateurControle | null {
  const lu = indicateurBrutSchema.safeParse(brut);
  if (!lu.success) return null;
  const { numero, nom, statut, libelle, resume, points } = lu.data;
  if (nom === undefined || statut === undefined || numero.trim() === '') return null;
  return {
    numero: numero.trim(),
    nom,
    statut: { code: statut, libelle: libelle ?? LIBELLES_CONNUS[statut] ?? libelleDuCode(statut) },
    ...(resume === undefined ? {} : { resume }),
    points: chacun(points, lirePoint),
  };
}

/** The raw indicator of this number, among the ones of an entry of `noyaux[]`. */
function brutDuNumero(
  indicateurs: readonly unknown[],
  numero: string,
): Record<string, unknown> | null {
  for (const brut of indicateurs) {
    if (typeof brut !== 'object' || brut === null) continue;
    const ligne = brut as Record<string, unknown>;
    if (String(ligne.numero ?? '').trim() === numero) return ligne;
  }
  return null;
}

/* ── The details of the cards ────────────────────────────────────────────────────── */

const ajoutBrutSchema = z.object({ type: texte, detail: texte, passage: texte });

/** Indicator 2.3, `elements[]`: the type of each addition, what it does, where it is. */
function lireAjouts(indicateur: Record<string, unknown> | null): AjoutControle[] | null {
  if (indicateur === null || !Array.isArray(indicateur.elements)) return null;
  return chacun(indicateur.elements, (brut): AjoutControle | null => {
    const lu = ajoutBrutSchema.safeParse(brut);
    if (!lu.success || lu.data.type === undefined) return null;
    const { type, detail, passage } = lu.data;
    return {
      // TO VALIDATE(#10): no dictionary of these types in Tahiti; the code stands.
      type: { code: type, libelle: libelleDuCode(type) },
      ...(detail === undefined ? {} : { detail }),
      ...(passage === undefined ? {} : { passage }),
    };
  });
}

const affirmationBrutSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String).optional(),
  etat: texte,
  texte: texte,
  detail: texte,
  passage: texte,
});

const inventaireSchema = z.object({
  inventaire: z.object({
    affirmations: z.array(z.unknown()),
  }),
});

/** The affirmations of the inventory (`inventaire.affirmations[]`), their text by identifier. */
function lireInventaire(resultat: unknown): ReadonlyMap<string, string> {
  const lu = inventaireSchema.safeParse(resultat);
  const textes = new Map<string, string>();
  if (!lu.success) return textes;
  for (const brut of lu.data.inventaire.affirmations) {
    const element = affirmationBrutSchema.safeParse(brut);
    if (element.success && element.data.id !== undefined && element.data.texte !== undefined) {
      textes.set(element.data.id, element.data.texte);
    }
  }
  return textes;
}

/**
 * Indicator 2.2, `elements[]`: each affirmation of the text of origin and its state. Its text
 * is the one of the element, or of the inventory when the element has none.
 */
function lireAffirmations(
  indicateur: Record<string, unknown> | null,
  inventaire: ReadonlyMap<string, string>,
): AffirmationControle[] | null {
  if (indicateur === null || !Array.isArray(indicateur.elements)) return null;
  return chacun(indicateur.elements, (brut): AffirmationControle | null => {
    const lu = affirmationBrutSchema.safeParse(brut);
    if (!lu.success || lu.data.etat === undefined) return null;
    const { id, etat, detail, passage } = lu.data;
    const libelle = lu.data.texte ?? (id === undefined ? undefined : inventaire.get(id));
    if (libelle === undefined) return null;
    return {
      texte: libelle,
      // TO VALIDATE(#10): no dictionary of these states in Tahiti; the code stands.
      etat: { code: etat, libelle: libelleDuCode(etat) },
      ...(detail === undefined ? {} : { detail }),
      ...(passage === undefined ? {} : { passage }),
    };
  });
}

const nombre = z.number().finite().nonnegative();

const longueurBruteSchema = z.object({
  nombre,
  min: nombre,
  max: nombre,
  unite: texte,
  ecart_pct: z.number().finite().optional().catch(undefined),
});

const longueursSchema = z.object({
  longueurs: z.object({ source: nombre.optional().catch(undefined), unite_source: texte }),
});

/**
 * Indicator 3.1: the length of the translation (`noyaux[].longueur`, or the `mesure` of the
 * indicator when the entry has none) and the length of the text of origin (`longueurs.source`).
 */
function lireLongueur(
  entree: Record<string, unknown>,
  indicateur: Record<string, unknown> | null,
  resultat: unknown,
): LongueurControle | null {
  const lu = longueurBruteSchema.safeParse(entree.longueur ?? indicateur?.mesure);
  if (!lu.success || lu.data.min > lu.data.max) return null;
  const { nombre: mots, min, max, ecart_pct } = lu.data;
  const longueurs = longueursSchema.safeParse(resultat);
  const { source, unite_source } = longueurs.success ? longueurs.data.longueurs : {};
  const unite = lu.data.unite ?? unite_source;
  // A length without its unit says nothing: the part is left out rather than guessed.
  if (unite === undefined) return null;
  return {
    nombre: mots,
    min,
    max,
    unite,
    ...(ecart_pct === undefined ? {} : { ecartPct: ecart_pct }),
    // The source is only comparable in the same unit.
    ...(source === undefined || (unite_source !== undefined && unite_source !== unite)
      ? {}
      : { source }),
  };
}

const entier = z.number().int().nonnegative();

const miseEnFormeBruteSchema = z.object({
  paragraphes: z.object({ source: entier, produit: entier }).optional().catch(undefined),
  titre_invente: z.boolean().optional().catch(undefined),
  markdown_ajoute: z.boolean().optional().catch(undefined),
  residus: liste,
});

/** Indicator 3.2: the paragraphs, an invented title, added markdown, leftovers. */
function lireMiseEnForme(indicateur: Record<string, unknown> | null): MiseEnFormeControle | null {
  if (indicateur === null) return null;
  const lu = miseEnFormeBruteSchema.safeParse(indicateur);
  if (!lu.success) return null;
  const { paragraphes, titre_invente, markdown_ajoute, residus } = lu.data;
  return {
    ...(paragraphes === undefined
      ? {}
      : { paragraphes: { origine: paragraphes.source, traduction: paragraphes.produit } }),
    ...(titre_invente === undefined ? {} : { titreInvente: titre_invente }),
    ...(markdown_ajoute === undefined ? {} : { markdownAjoute: markdown_ajoute }),
    // TO VALIDATE(#10): `residus` was always empty in what was seen; only texts are kept.
    residus: chacun(residus, (brut) =>
      typeof brut === 'string' && brut.trim() !== '' ? brut.trim() : null,
    ),
  };
}

const pourcentage = z.number().finite().min(0).max(100);

const reecritureBruteSchema = z.object({
  taux_recopie: pourcentage,
  seuil: pourcentage.optional().catch(undefined),
});

/** Indicator 3.3: the share of the text of origin the translation copied, and the threshold. */
function lireReecriture(indicateur: Record<string, unknown> | null): ReecritureControle | null {
  const lu = reecritureBruteSchema.safeParse(indicateur);
  if (!lu.success) return null;
  const { taux_recopie, seuil } = lu.data;
  return { tauxRecopie: taux_recopie, ...(seuil === undefined ? {} : { seuil }) };
}

/* ── The counters ────────────────────────────────────────────────────────────────── */

const comptesBrutsSchema = z.record(z.string(), z.unknown());

/**
 * `compte_statuts`: "Bloquant", "À relire" and "OK" always (zero when Tahiti left one out), then
 * any other status that is not zero. A label is the one an indicator carries for that status,
 * or, failing that, the label Tahiti writes for it (or its code).
 */
function lireComptes(
  brut: unknown,
  indicateurs: readonly IndicateurControle[],
): CompteStatut[] | null {
  const lu = comptesBrutsSchema.safeParse(brut);
  if (!lu.success) return null;
  const valeurs = new Map<string, number>();
  for (const [code, valeur] of Object.entries(lu.data)) {
    if (typeof valeur === 'number' && Number.isInteger(valeur) && valeur >= 0) {
      valeurs.set(code, valeur);
    }
  }
  if (valeurs.size === 0) return null;

  const statut = (code: string): CodeLibelle => ({
    code,
    libelle:
      indicateurs.find((indicateur) => indicateur.statut.code === code)?.statut.libelle ??
      LIBELLES_CONNUS[code] ??
      libelleDuCode(code),
  });
  const autres = [...valeurs].filter(
    ([code, valeur]) => valeur > 0 && !(STATUTS_AFFICHES as readonly string[]).includes(code),
  );
  return [
    ...STATUTS_AFFICHES.map((code) => ({ statut: statut(code), nombre: valeurs.get(code) ?? 0 })),
    ...autres.map(([code, valeur]) => ({ statut: statut(code), nombre: valeur })),
  ];
}

/* ── The changes the translation declares ───────────────────────────────────────── */

export const TABLE_TRADUCTION = 'rose_task_v5_traduction';

const changementBrutSchema = z.object({ type: texte, passage: texte, description: texte });

/** One change of `changements[]`; `null` without a type and a passage. */
function lireChangement(brut: unknown): ChangementTraduction | null {
  const lu = changementBrutSchema.safeParse(brut);
  if (!lu.success) return null;
  const { type, passage, description } = lu.data;
  if (type === undefined || passage === undefined) return null;
  return {
    // TO VALIDATE(#28): no dictionary of these types in Tahiti; the code stands.
    type: { code: type, libelle: libelleDuCode(type) },
    passage,
    ...(description === undefined ? {} : { description }),
  };
}

const resultatTraductionSchema = z.object({ noyau: noyauCodeSchema.optional().catch(undefined) });

/** The changes of a `result_json` of a translation sub-task, or `null` when it has none to read. */
export function lireChangements(resultat: unknown): ChangementTraduction[] | null {
  if (typeof resultat !== 'object' || resultat === null) return null;
  const { changements } = resultat as { changements?: unknown };
  if (!Array.isArray(changements)) return null;
  return chacun(changements, lireChangement);
}

/** A row of `rose_task_v5_traduction`, as far as this file reads it. */
export interface LigneTraduction {
  id: string;
  result_json?: unknown;
}

/**
 * The changes of each noyau, from the translation sub-tasks of a launch: the noyau is the one the
 * entry of the control names (`traduction_task_id`) or, failing that, the `noyau` of the
 * `result_json`. A noyau with several rows keeps the most recent one (the highest identifier).
 */
export function changementsParNoyau(
  lignes: readonly LigneTraduction[],
  entreesControle: readonly unknown[],
): Map<string, ChangementTraduction[] | null> {
  const noyauDeLaTache = new Map<string, string>();
  for (const entree of entreesControle) {
    if (typeof entree !== 'object' || entree === null) continue;
    const { noyau, traduction_task_id: tache } = entree as Record<string, unknown>;
    if (typeof noyau === 'string' && (typeof tache === 'string' || typeof tache === 'number')) {
      noyauDeLaTache.set(String(tache), noyau);
    }
  }
  const retenues = new Map<string, { id: number; changements: ChangementTraduction[] | null }>();
  for (const ligne of lignes) {
    const resultat = lireJson(ligne.result_json);
    const nom = resultatTraductionSchema.safeParse(resultat);
    const noyau = noyauDeLaTache.get(ligne.id) ?? (nom.success ? nom.data.noyau : undefined);
    if (noyau === undefined) continue;
    const id = Number(ligne.id);
    const deja = retenues.get(noyau);
    if (deja === undefined || id > deja.id) {
      retenues.set(noyau, { id, changements: lireChangements(resultat) });
    }
  }
  return new Map([...retenues].map(([noyau, { changements }]) => [noyau, changements]));
}

const ligneTraductionSchema = z.object({ id: idSchema, result_json: z.unknown().optional() });

/** More sub-tasks than a launch ever has (six noyaux, a few retries). */
const LIMITE_TRADUCTIONS = 50;

/** One read, for every noyau; a service that cannot be reached for it gives no changes. */
async function lireLignesTraduction(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  tacheId: string,
): Promise<LigneTraduction[]> {
  try {
    return await lireLignes(
      appel,
      config,
      jeton,
      {
        table: TABLE_TRADUCTION,
        fields: ['id', 'result_json'],
        where: ['h2_task_main_id', Number(tacheId), '='],
        limit: LIMITE_TRADUCTIONS,
        lang: CODE_LANGUE,
      },
      ligneTraductionSchema,
    );
  } catch (erreur) {
    if (erreur instanceof ServiceIndisponibleError) return [];
    throw erreur;
  }
}

/* ── The control ─────────────────────────────────────────────────────────────────── */

const entreeBruteSchema = z
  .object({
    noyau: noyauCodeSchema,
    indicateurs: liste,
  })
  .passthrough();

/** The control of one noyau, or `null` when the entry names no known noyau. */
function lireNoyau(brut: unknown, resultat: unknown): ControleNoyau | null {
  const lu = entreeBruteSchema.safeParse(brut);
  if (!lu.success) return null;
  const entree = lu.data;
  const indicateurs = chacun(entree.indicateurs, lireIndicateur);
  const du = (numero: string) => brutDuNumero(entree.indicateurs, numero);
  // What leaves the client goes through the shared schema, like every answer of Tahiti.
  const controle = controleNoyauSchema.safeParse({
    noyau: entree.noyau,
    comptes: lireComptes(entree.compte_statuts, indicateurs),
    indicateurs,
    ajouts: lireAjouts(du('2.3')),
    affirmations: lireAffirmations(du('2.2'), lireInventaire(resultat)),
    longueur: lireLongueur(entree, du('3.1'), resultat),
    miseEnForme: lireMiseEnForme(du('3.2')),
    reecriture: lireReecriture(du('3.3')),
  });
  return controle.success ? controle.data : null;
}

const controleBrutSchema = z.object({
  success: z.boolean().optional(),
  noyaux: z.array(z.unknown()),
});

/** The control a `result_json` holds, or `null` when it is not the answer of a control. */
export function lireResultatControle(resultat: unknown): ControleTraduction | null {
  const lu = controleBrutSchema.safeParse(resultat);
  if (!lu.success || lu.data.success === false) return null;
  const noyaux = chacun(lu.data.noyaux, (brut) => lireNoyau(brut, resultat));
  return noyaux.length === 0 ? null : { noyaux };
}

/**
 * Reads the quality control of a finished translation, or `null` when Tahiti has none that can
 * be read. `types` gives the names of the Rosetta task types (kept by the caller for the
 * session), read only when the sub-task has to be found through `h2_task_main_id`.
 */
export async function lireControle(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  tacheId: string,
  types: () => Promise<CorrespondanceTypes>,
): Promise<ControleTraduction | null> {
  let id = controleDuResultat(await lireResultat(appel, config, jeton, TABLE_TACHE, tacheId));
  // The launch does not name it (its `result_json` is null, or older): the sub-tasks say.
  if (id === null) {
    const [sousTaches, correspondance] = await Promise.all([
      lireSousTaches(appel, config, jeton, tacheId),
      types(),
    ]);
    id = derniereDuType(sousTaches, correspondance, TABLE_CONTROLE);
  }
  if (id === null) return null;
  const resultat = await lireResultat(appel, config, jeton, TABLE_CONTROLE, id);
  const controle = lireResultatControle(resultat);
  if (controle === null) return null;
  // One more read, shared by every noyau and every tab (#28).
  const { noyaux: entrees } = resultat as { noyaux: unknown[] };
  const parNoyau = changementsParNoyau(
    await lireLignesTraduction(appel, config, jeton, tacheId),
    entrees,
  );
  return {
    noyaux: controle.noyaux.map((noyau) => ({
      ...noyau,
      changements: parNoyau.get(noyau.noyau) ?? null,
    })),
  };
}

/* ── The launches of a document (#34) ────────────────────────────────────────────── */

/** What reading the control of one launch of a document gave. */
export interface LectureLancement {
  id: string;
  /** The noyaux the launch targets. */
  noyaux: readonly NoyauCode[];
  /**
   * Its control, `null` when it has none that can be read (an old plugin, an interrupted launch,
   * a failed control: not an error), `'illisible'` when the read itself failed (network, timeout,
   * service error: the control may exist).
   */
  controle: ControleTraduction | null | 'illisible';
}

/**
 * The control of a text from the control of each of its launches: for each noyau the entry of the
 * most recent launch (the highest identifier) that has one, in the order of the charter. A launch
 * that could not be read leaves the noyaux it targets, and that no other launch controlled, in
 * `noyauxNonLus`: they may have a control, unlike the ones no launch covers. `listeIncomplete`
 * says the launches of the document could not be listed, so that every noyau with no entry is
 * unknown. `null` when there is nothing to show and nothing unknown. Pure: no verdict is computed.
 */
export function fusionnerControles(
  lectures: readonly LectureLancement[],
  listeIncomplete = false,
): ControleTraduction | null {
  const recentes = [...lectures].sort((a, b) => Number(b.id) - Number(a.id));
  const retenus = new Map<NoyauCode, ControleNoyau>();
  for (const lecture of recentes) {
    if (lecture.controle === null || lecture.controle === 'illisible') continue;
    for (const entree of lecture.controle.noyaux) {
      if (!retenus.has(entree.noyau)) retenus.set(entree.noyau, entree);
    }
  }
  const nonLus = new Set<NoyauCode>();
  for (const lecture of recentes) {
    if (lecture.controle !== 'illisible') continue;
    for (const noyau of lecture.noyaux) if (!retenus.has(noyau)) nonLus.add(noyau);
  }
  if (listeIncomplete) {
    for (const noyau of NOYAU_CODES) if (!retenus.has(noyau)) nonLus.add(noyau);
  }
  if (retenus.size === 0 && nonLus.size === 0) return null;
  const dans = <T>(noyaux: ReadonlyMap<NoyauCode, T> | ReadonlySet<NoyauCode>) =>
    NOYAU_CODES.filter((noyau) => noyaux.has(noyau));
  return {
    noyaux: dans(retenus).flatMap((noyau) => {
      const entree = retenus.get(noyau);
      return entree === undefined ? [] : [entree];
    }),
    ...(nonLus.size === 0 ? {} : { noyauxNonLus: dans(nonLus) }),
  };
}
